/**
 * One-tap starting points.
 *
 * Not filters in the Instagram sense — each one is an ordinary set of the same
 * sliders, so tapping a look and then adjusting it works exactly the way you
 * would expect, and the sliders show where it put them. A look that could not
 * be reproduced by hand would be a black box in the middle of a tool whose
 * whole point is that you can see what it did.
 */

import type { Adjustments } from "./adjustments.js";
import { NEUTRAL } from "./adjustments.js";

export interface Look {
  name: string;
  adjustments: Adjustments;
}

export const LOOKS: readonly Look[] = [
  { name: "Original", adjustments: NEUTRAL },
  {
    name: "Punch",
    adjustments: { ...NEUTRAL, contrast: 22, saturation: 18, shadows: 8 },
  },
  {
    // Late afternoon, roughly: warmer, and lifted out of the shadows the way
    // low sun actually behaves.
    name: "Golden",
    adjustments: {
      ...NEUTRAL,
      warmth: 30,
      exposure: 6,
      shadows: 15,
      saturation: 8,
    },
  },
  {
    name: "Cool",
    adjustments: { ...NEUTRAL, warmth: -28, contrast: 10 },
  },
  {
    // Lifted blacks and less colour: the flat, faded look, which needs the
    // contrast *down* rather than the exposure up.
    name: "Faded",
    adjustments: { ...NEUTRAL, contrast: -18, shadows: 28, saturation: -12 },
  },
  {
    name: "Mono",
    adjustments: { ...NEUTRAL, monochrome: true, contrast: 12 },
  },
  {
    name: "Noir",
    adjustments: {
      ...NEUTRAL,
      monochrome: true,
      contrast: 45,
      highlights: -15,
      shadows: -10,
    },
  },
];

/** Whether a set of adjustments is exactly one of the looks, for highlighting. */
export function matchingLook(adjustments: Adjustments): string | null {
  const found = LOOKS.find(
    (look) =>
      look.adjustments.exposure === adjustments.exposure &&
      look.adjustments.contrast === adjustments.contrast &&
      look.adjustments.highlights === adjustments.highlights &&
      look.adjustments.shadows === adjustments.shadows &&
      look.adjustments.saturation === adjustments.saturation &&
      look.adjustments.warmth === adjustments.warmth &&
      look.adjustments.monochrome === adjustments.monochrome,
  );
  return found?.name ?? null;
}
